import { API_BASE_URL } from '../config/env';

export interface PasswordRecoveryRequestResult {
  sent: boolean;
}

export interface PasswordRecoveryVerifyResult {
  recoveryToken: string;
  expiresInMinutes: number;
}

export interface ApiError extends Error {
  status?: number;
  retryAfter?: number;
  retryAfterMinutes?: number;
}

const normalizeDigits = (value: string): string => value.replace(/\D/g, '');

async function readJson(response: Response): Promise<any> {
  return response.json().catch(() => ({}));
}

function buildApiError(response: Response, payload: any, fallback: string): Error {
  const error = new Error(payload.erro || payload.error || fallback);
  const retryAfterSeconds = Number(payload?.data?.retryAfterSeconds ?? response.headers.get('retry-after'));
  const retryAfterMinutes = Number(payload?.data?.retryAfterMinutes);

  (error as ApiError).status = response.status;
  (error as ApiError).retryAfter = Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : undefined;
  (error as ApiError).retryAfterMinutes = Number.isFinite(retryAfterMinutes)
    ? retryAfterMinutes
    : Number.isFinite(retryAfterSeconds)
      ? Math.max(1, Math.ceil(retryAfterSeconds / 60))
      : undefined;
  return error;
}

export async function requestPasswordRecovery(cpf: string): Promise<PasswordRecoveryRequestResult> {
  const response = await fetch(`${API_BASE_URL}/auth/password-recovery/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cpf: normalizeDigits(cpf) }),
  });

  const payload = await readJson(response);

  if (!response.ok) {
    throw buildApiError(
      response,
      payload,
      'Nao foi possivel iniciar a recuperacao de senha no momento.'
    );
  }

  return payload.data as PasswordRecoveryRequestResult;
}

export async function verifyPasswordRecoveryCode(
  cpf: string,
  code: string
): Promise<PasswordRecoveryVerifyResult> {
  const response = await fetch(`${API_BASE_URL}/auth/password-recovery/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      cpf: normalizeDigits(cpf),
      code: normalizeDigits(code),
    }),
  });

  const payload = await readJson(response);

  if (!response.ok) {
    throw buildApiError(response, payload, 'Codigo invalido ou expirado.');
  }

  return payload.data as PasswordRecoveryVerifyResult;
}

export async function confirmPasswordRecovery(
  recoveryToken: string,
  newPassword: string,
  newPasswordConfirm: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/password-recovery/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      recoveryToken,
      newPassword,
      newPasswordConfirm,
    }),
  });

  const payload = await readJson(response);

  if (!response.ok) {
    throw buildApiError(response, payload, 'Nao foi possivel redefinir a senha.');
  }
}
